import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => (
  <section className="pt-12 pb-8 bg-brand-cream" data-testid="contact-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <p className="section-subheading">Get in Touch</p>
        <h1 className="section-heading">Contact Us</h1>
        <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
          Have a question about Weaver's Nest? We'd love to hear from you. 
          Send us a message and we'll respond as soon as possible.
        </p>
      </motion.div>
    </div>
  </section>
);

const ContactInfo = () => (
  <div className="space-y-8">
    <div>
      <h2 className="font-heading text-2xl mb-6">Contact Information</h2>
      <p className="text-muted-foreground mb-8">
        Feel free to reach out via any of the channels below. 
        We typically respond within a few hours.
      </p>
    </div>
    
    <div className="space-y-6">
      <a 
        href="tel:+27832329940"
        className="flex items-start gap-4 group"
        data-testid="contact-phone"
      >
        <div className="w-12 h-12 rounded-full bg-brand-sage/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-sage/20 transition-colors">
          <Phone className="w-5 h-5 text-brand-sage" />
        </div>
        <div>
          <p className="font-medium text-brand-charcoal group-hover:text-brand-sage transition-colors">Phone</p>
          <p className="text-muted-foreground">+27 83 232 9940</p>
        </div>
      </a>
      
      <a 
        href="https://api.whatsapp.com/send?phone=%2B27832329940"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-start gap-4 group"
        data-testid="contact-whatsapp"
      >
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
          <MessageCircle className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <p className="font-medium text-brand-charcoal group-hover:text-green-600 transition-colors">WhatsApp</p>
          <p className="text-muted-foreground">Chat with us directly</p>
        </div>
      </a>
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-brand-sage/10 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-brand-sage" />
        </div>
        <div>
          <p className="font-medium text-brand-charcoal">Email</p>
          <p className="text-muted-foreground">weaversnest@sardiniabay.co.za</p>
        </div>
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-brand-sage/10 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-brand-sage" />
        </div>
        <div>
          <p className="font-medium text-brand-charcoal">Location</p>
          <p className="text-muted-foreground">Sardinia Bay, Gqeberha</p>
          <p className="text-muted-foreground">Eastern Cape, South Africa</p>
        </div>
      </div>
    </div>
    
    {/* Quick Actions */}
    <div className="pt-8 border-t border-brand-border">
      <h3 className="font-medium mb-4">Quick Actions</h3>
      <div className="flex flex-wrap gap-3">
        <a 
          href="https://api.whatsapp.com/send?phone=%2B27832329940"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 text-white px-6 py-3 rounded-full font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp Us
        </a>
        <a 
          href="tel:+27832329940"
          className="btn-secondary"
        >
          <Phone className="w-4 h-4 mr-2" />
          Call Now
        </a>
      </div>
    </div>
  </div>
);

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "General Inquiry",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-2xl border border-brand-border">
      <h2 className="font-heading text-2xl mb-6">Send us a Message</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="mt-2"
              required
              data-testid="contact-name"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="mt-2"
              placeholder="+27..."
              data-testid="contact-phone-input"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="mt-2"
            required
            data-testid="contact-email"
          />
        </div>
        
        <div>
          <Label htmlFor="subject">Subject</Label>
          <Select 
            value={formData.subject} 
            onValueChange={(value) => setFormData({...formData, subject: value})}
          >
            <SelectTrigger className="mt-2" data-testid="contact-subject">
              <SelectValue placeholder="Select a subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              <SelectItem value="Booking Question">Booking Question</SelectItem>
              <SelectItem value="Wedding/Bridal Inquiry">Wedding/Bridal Inquiry</SelectItem>
              <SelectItem value="Special Request">Special Request</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="message">Message *</Label>
          <Textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            className="mt-2"
            placeholder="How can we help you?"
            rows={5}
            required
            data-testid="contact-message"
          />
        </div>
        
        <Button 
          type="submit"
          disabled={loading}
          className="btn-primary w-full"
          data-testid="contact-submit"
        >
          {loading ? (
            "Sending..."
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

const MapSection = () => (
  <section className="py-12 md:py-20 bg-white" data-testid="contact-map">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-8"
      >
        <h2 className="font-heading text-3xl">Find Us</h2>
        <p className="text-muted-foreground mt-2">
          Sardinia Bay, Gqeberha (Port Elizabeth), South Africa
        </p>
      </motion.div>
      
      <div className="rounded-2xl overflow-hidden border border-brand-border h-[400px]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3312.123456789!2d25.6521!3d-34.0355!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sSardinia%20Bay!5e0!3m2!1sen!2sza!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Weaver's Nest Location"
        />
      </div>
    </div>
  </section>
);

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <HeroSection />
      
      <section className="py-12 md:py-20 bg-brand-cream">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <ContactInfo />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>
      
      <MapSection />
    </div>
  );
}
